/**
 * The "Billing" panel on the admin user card (#1705).
 *
 * Reads the observational `subscriptionMirrors` collection through the console's
 * own endpoint, filtered to one uid. It answers what `/entitlements` structurally
 * cannot: a subscription's NON-ACTIVE provider status (`past_due`, `canceled`,
 * `incomplete`), and subscriptions that no longer grant anything at all — the
 * "why did this user lose access?" case. The entitlements payload drops an
 * entitlement that is inactive from every source, so that history is invisible
 * on the access side by construction.
 *
 * This section is DISPLAY ONLY. It never decides access, and it is not the
 * authority for what the user can do — the Access State section above it is.
 *
 * ## Current, past, and the history the mirror does not hold
 *
 * Rows are partitioned by `isPastSubscription`, and past ones fold away so they
 * cannot swamp what the user is paying for now. "Ended, but still granting"
 * fires ONLY on past rows, so a plain fold would collapse this panel's loudest
 * alarm by default — those rows are therefore HOISTED above the fold rather
 * than the fold being forced open, which on a user with one live and
 * twenty-five dead subscriptions would have buried the live one under history
 * to show one alarm.
 *
 * Neither group is only what the mirror holds. `resolveUnmirroredGrants`
 * recovers subscription-shaped grants the ENTITLEMENT record still carries that
 * no mirror row accounts for — pre-migration ones, and any never backfilled.
 * Without it this panel reports "no subscription is mirrored against this uid"
 * on a user whose own access record names a Stripe subscription that ran for
 * three months. Those grants are split on their own `isActive`: a LIVE one is
 * current, not history, and belongs beside the mirrored current rows. In an
 * environment where the reconcile has not run, that is every subscriber.
 *
 * ## Four ways this section can be empty or short, none of them "no subscriptions"
 *
 * 1. **The mirror is only as complete as the reconcile that filled it.** It is
 *    populated by best-effort webhook dual-writes plus a reconcile that has run
 *    per environment (see `docs/setup/per-environment-manual-steps.md`). Before
 *    that reconcile runs in an environment, a real subscriber can have no row.
 * 2. **History does not go back to the beginning.** The Stripe reconcile pages
 *    live Stripe with `status: 'all'`, so canceled subscriptions ARE
 *    backfilled — but one Stripe no longer returns at all cannot be, and
 *    nothing in the data marks the gap. (This does not apply to supapass, whose
 *    reconcile reads the local `/externalSubscriptions` collection rather than
 *    a provider API.)
 * 3. **A subscription not linked to this uid is invisible here.** The mirror
 *    stores `uid: ''` until an email is matched to an account, and this query
 *    filters on uid. The webhook stamps `matchedUid` by bare email lookup, so
 *    this is normally the ordinary awaiting-signup state — but there is one
 *    narrower case where an EXISTING user stays unlinked: the sign-in apply
 *    returns early when none of their products is catalogued
 *    (`externalSubscriptionService`, the no-bundle-mapping `continue`, which
 *    precedes the `matchedUid` backfill), so a user who had no account when the
 *    webhook arrived AND whose products are all uncatalogued shows nothing
 *    here while actively paying.
 * 4. **A tombstoned row is skipped by design.** A subscription naming a
 *    scrubbed user is deliberately not projected (#1780).
 *
 * Three of the four are on screen — the lead carries the two that change what
 * an admin does next, the disclosure carries the rest. (4) is deliberately NOT
 * shown: the user whose card this is has not been deleted, so it can never
 * explain the emptiness in front of the reader.
 */

import { Link } from 'react-router-dom';
import { useAdminSubscriptionsList } from '../../hooks/useAdminSubscriptions';
import {
  useAdminEntitlementsList,
  type EntitlementListResponse,
} from '../../hooks/useAdminEntitlementsList';
import {
  isActionableGap,
  resolveCatalogCoverage,
} from '../../lib/utils/catalogCoverage';
import {
  billingAccessDivergence,
  isPastSubscription,
  resolveAccessLink,
  resolveUnmirroredGrants,
  type AccessLink,
  type BillingAccessDivergence,
  type UnmirroredGrant,
} from '../../lib/utils/billingAccessBridge';
import type { UserEntitlements } from '../../lib/types/entitlement';
import {
  NORMALIZED_STATE_LABELS,
  type AdminSubscriptionInfo,
} from '../../lib/types/subscriptionMirror';
import { CoverageCell, NormalizedStateBadge } from './subscriptionDisplay';
import { formatRenewal, formatUtc } from '../../lib/utils/subscriptionFormat';
import { provenanceLabel } from '../../lib/utils/entitlementProvenance';

/** One page is deliberate — see the `hasMore` note rendered below. */
const PAGE_SIZE = 100;

interface UserSubscriptionsSectionProps {
  uid: string;
  /** The card's entitlements, for the access join. `undefined` while loading. */
  entitlements: UserEntitlements | undefined;
}

function AccessLinkLine({
  link,
  provider,
}: {
  link: AccessLink;
  provider: string;
}) {
  if (link.state === 'unknown') {
    return <span className="text-xs text-gray-400">Access not loaded yet</span>;
  }
  if (link.state === 'granting') {
    return (
      <span
        className="text-xs text-green-700 dark:text-green-400"
        title="Read from the access record, not from this subscription."
      >
        Currently giving the user {link.entitlementIds.join(', ')}
      </span>
    );
  }
  if (link.state === 'ambiguous') {
    return (
      <span
        className="text-xs text-gray-500 dark:text-gray-400"
        title="An older grant from this provider doesn't record which subscription it came from, so it can't be matched to this one."
      >
        This user has {provenanceLabel(provider)} access, but we can&apos;t tell
        which subscription grants it
      </span>
    );
  }
  if (link.state === 'recorded-inactive') {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400">
        Linked to {link.entitlementIds.join(', ')}, but not giving it
      </span>
    );
  }
  return (
    <span
      className="text-xs text-gray-500 dark:text-gray-400"
      title="Access that has ended from every source isn't listed at all, so this can't rule out an old record."
    >
      Not giving any access
    </span>
  );
}

/**
 * Both directions the two stores can disagree in. Worded as observations, not
 * verdicts — see `billingAccessDivergence` for why a disagreement here is
 * usually a real gap rather than a moment in flight, and for the states that
 * are deliberately never flagged.
 */
function DivergenceBadge({
  divergence,
}: {
  divergence: NonNullable<BillingAccessDivergence>;
}) {
  const copy =
    divergence === 'live-not-granting'
      ? {
          label: 'Active, but no access',
          title:
            "The provider says this is live, but the user doesn't have the access it should give. Two usual causes: the product isn't in the catalog (copy the product id from this row and send it to engineering — the catalog can't be edited from this tool), or the access step didn't finish. Check the product first.",
        }
      : {
          // Always a provider-sourced grant: the divergence is computed from
          // `subscriptions[]` alone, never from the manual grant. So pointing
          // an admin at "Add or remove manual access" would send them to a
          // control that cannot touch it.
          label: 'Ended, but access not removed',
          title:
            "This subscription is over but its access is still there. It can't be fixed from this screen — report it.",
        };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
      title={copy.title}
    >
      {copy.label}
    </span>
  );
}

interface DecoratedRow {
  row: AdminSubscriptionInfo;
  link: AccessLink;
  divergence: BillingAccessDivergence;
}

/** One mirror row: what the provider says, and how it lands on access. */
function SubscriptionCard({
  entry,
  catalog,
}: {
  entry: DecoratedRow;
  catalog: EntitlementListResponse | null | undefined;
}) {
  const { row, link, divergence } = entry;
  const coverage = resolveCatalogCoverage(
    row,
    catalog?.externalBundles,
    catalog?.bundles
  );
  return (
    <li className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {provenanceLabel(row.provider)}
        </span>
        <NormalizedStateBadge
          state={row.normalizedStatus}
          rawStatus={row.status}
        />
        {/*
          Only when it adds something. On most rows the provider's word is the
          badge's own label in different case ("Active" / "ACTIVE"), which read
          as the same value printed twice by accident. It is never lost: the
          badge carries it in its tooltip.
        */}
        {row.status.toLowerCase() !==
          NORMALIZED_STATE_LABELS[row.normalizedStatus]?.toLowerCase() && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {provenanceLabel(row.provider)} says &ldquo;{row.status}&rdquo;
          </span>
        )}
        {divergence && <DivergenceBadge divergence={divergence} />}
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="inline text-gray-500 dark:text-gray-400">Renewal: </dt>
          <dd className="inline text-gray-700 dark:text-gray-200">
            {formatRenewal(row)}
          </dd>
        </div>
        <div>
          <dt className="inline text-gray-500 dark:text-gray-400">
            Covered until:{' '}
          </dt>
          <dd className="inline text-gray-700 dark:text-gray-200">
            {row.currentPeriodEnd ? formatUtc(row.currentPeriodEnd) : '—'}
          </dd>
        </div>
        <div className="col-span-2">
          <dt
            className="inline text-gray-500 dark:text-gray-400"
            title="What this product unlocks for whoever holds it — not what this user has. The line below says what they actually have."
          >
            Unlocks:{' '}
          </dt>
          <dd className="inline">
            <CoverageCell
              coverage={coverage}
              actionable={isActionableGap(row, coverage)}
            />
          </dd>
        </div>
      </dl>

      <div className="mt-2">
        <AccessLinkLine link={link} provider={row.provider} />
      </div>

      <div className="mt-2 font-mono text-xs text-gray-400 dark:text-gray-500 break-all">
        {row.providerSubscriptionId}
        {row.productIds.length > 0 && <> · {row.productIds.join(', ')}</>}
      </div>
      <div className="text-xs text-gray-400 dark:text-gray-500">
        mirror updated {formatUtc(row.updatedAt)}
      </div>
    </li>
  );
}

/**
 * A grant the access record carries that no mirror row accounts for.
 *
 * Deliberately styled apart from a mirror row — dashed border, no state badge —
 * because every other card in this panel is something a PROVIDER said, and this
 * one is not.
 */
function UnmirroredGrantCard({ grant }: { grant: UnmirroredGrant }) {
  return (
    <li className="p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {provenanceLabel(grant.provider)}
        </span>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
          title="In the access record, with no mirror row against this uid."
        >
          No billing record
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          we only know about this from their access
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="inline text-gray-500 dark:text-gray-400">Status: </dt>
          <dd className="inline text-gray-700 dark:text-gray-200">
            {grant.isActive ? 'active' : 'ended'}
          </dd>
        </div>
        <div>
          <dt className="inline text-gray-500 dark:text-gray-400">
            {grant.expiresAt && new Date(grant.expiresAt).getTime() < Date.now()
              ? 'Expired:'
              : 'Expires:'}{' '}
          </dt>
          <dd className="inline text-gray-700 dark:text-gray-200">
            {grant.expiresAt ? formatUtc(grant.expiresAt) : 'non-expiring'}
          </dd>
        </div>
        {grant.purchaseDate && (
          <div className="col-span-2">
            <dt className="inline text-gray-500 dark:text-gray-400">
              Started:{' '}
            </dt>
            <dd className="inline text-gray-700 dark:text-gray-200">
              {formatUtc(grant.purchaseDate)}
            </dd>
          </div>
        )}
        <div className="col-span-2">
          <dt className="inline text-gray-500 dark:text-gray-400">For: </dt>
          <dd className="inline text-gray-700 dark:text-gray-200">
            {grant.entitlementIds.join(', ')}
          </dd>
        </div>
      </dl>

      <div className="mt-2 font-mono text-xs text-gray-400 dark:text-gray-500 break-all">
        {grant.providerSubscriptionId ?? 'No subscription id'}
        {grant.productIdentifier && <> · {grant.productIdentifier}</>}
      </div>
    </li>
  );
}

export function UserSubscriptionsSection({
  uid,
  entitlements,
}: UserSubscriptionsSectionProps) {
  // A blank uid must never reach the API: the client drops a falsy `uid` from
  // the query string, so the request would become an unfiltered listing of the
  // WHOLE collection — another hundred users' subscriptions rendered on one
  // user's card. Written totally rather than as `uid.trim()` so a value that
  // violates the prop type crashes nothing.
  const hasUid = Boolean(uid && uid.trim());

  const { data, isLoading, isError, error } = useAdminSubscriptionsList(
    { uid, limit: PAGE_SIZE },
    // Its own cache namespace: the console can build an identical params
    // object, and sharing the entry would make opening this card refetch every
    // page the console had loaded.
    { enabled: hasUid, scope: 'user-card' }
  );
  const { data: catalog, error: catalogError } = useAdminEntitlementsList();

  const page = data?.pages[0];
  const rows: AdminSubscriptionInfo[] = page?.subscriptions ?? [];

  const decorated: DecoratedRow[] = rows.map(row => {
    const link = resolveAccessLink(row, entitlements);
    return { row, link, divergence: billingAccessDivergence(row, link) };
  });
  const current = decorated.filter(d => !isPastSubscription(d.row));
  const past = decorated.filter(d => isPastSubscription(d.row));

  // Only sound on a complete list: past page one, "no mirror row matches" may
  // simply mean "not on the page we fetched". `page === undefined` is NOT
  // complete either — `!undefined` is `true`, which would assert completeness
  // over a list that has not loaded.
  const unmirrored = resolveUnmirroredGrants(rows, entitlements, {
    complete: page !== undefined && !page.hasMore,
  });

  // An unmirrored grant is not automatically history. `isActive` on the wire
  // is `record.isActive === true && not expired`, so a live one means the user
  // HAS access through a subscription the mirror has no row for — the ordinary
  // state of every subscriber in an environment where the reconcile has not
  // been run (see the register: dev only, today). Filing those under "past"
  // would print
  // "Nothing current. Every subscription below has ended." over a live paying
  // customer: the mirror image of the bug this panel exists to remove.
  const unmirroredCurrent = unmirrored.filter(g => g.isActive);
  const unmirroredPast = unmirrored.filter(g => !g.isActive);

  // The two states where a charge genuinely failed — `grace` is the retry
  // window, `on_hold` is retries exhausted. Worth its own count: it is the most
  // support-relevant thing a "current" subscription can be, and the plain count
  // of current subscriptions hides it.
  const failingCount = current.filter(
    d =>
      d.row.normalizedStatus === 'grace' || d.row.normalizedStatus === 'on_hold'
  ).length;

  const stillGranting = past.filter(d => d.divergence);
  const settledPast = past.filter(d => !d.divergence);
  const currentCount = current.length + unmirroredCurrent.length;
  const pastCount = past.length + unmirroredPast.length;
  const foldedCount = settledPast.length + unmirroredPast.length;
  const nothingAtAll =
    current.length === 0 && past.length === 0 && unmirrored.length === 0;

  return (
    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
      <div className="mb-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Billing
        </h4>
        {/*
          "last reported", not "says": this is a stored copy that can run
          behind, and a support admin meeting "the customer says they cancelled
          but this says active" needs that in the sentence they actually read,
          not behind a fold.
        */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          What Stripe and Supapass last reported. Doesn&apos;t control access —
          the Access panel above does.
        </p>
        <details className="mt-1">
          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
            Technical details
          </summary>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This is our stored copy of what the provider last told us, not a
            live lookup, so it can run behind after a change. It can also be
            incomplete: subscriptions that ended long ago were never brought
            across, and a subscription we only know about from the user&apos;s
            access disappears from here once that access has fully lapsed. Some
            subscriptions have no billing record at all: older ones didn&apos;t
            record a subscription id.
          </p>
        </details>
      </div>

      {!hasUid && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No user id, so billing wasn&apos;t looked up. Nothing here is a
          statement about this user.
        </p>
      )}

      {hasUid && isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Loading billing…
        </p>
      )}

      {isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          <strong>Couldn&apos;t load billing.</strong> Access above is
          unaffected. ({error?.message})
        </p>
      )}

      {hasUid && !isLoading && !isError && (
        <>
          {catalogError && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
              <strong>Couldn&apos;t load the catalog</strong>, so
              &ldquo;Unlocks&rdquo; is blank on every row below. Blank means
              unknown, not &ldquo;nothing&rdquo;. ({catalogError.message})
            </p>
          )}

          {nothingAtAll ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p className="font-medium text-gray-700 dark:text-gray-200">
                No subscriptions found for this user.
              </p>
              {/*
                Both lines above the fold answer a support question. The first
                is the commonest cause of a false negative; the second is the
                commonest scenario of all — "I subscribed just now and can't get
                in" — which every earlier draft left with no answer anywhere on
                an empty panel. No timescale is promised: nothing in the system
                bounds the lag.
              */}
              <p className="text-xs mt-1">
                They may still have one — a subscription under a different email
                won&apos;t show here.{' '}
                <Link
                  to="/admin/subscriptions?orphan=1"
                  className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                >
                  Search subscriptions with no linked user
                </Link>
              </p>
              <p className="text-xs mt-1">
                A subscription started just now may not have come through yet —
                check again shortly.
              </p>
              {/*
                Task-framed, not "Technical details" — an admin looking at an
                empty panel is asking exactly this question, and a fold labelled
                for engineers is one they are trained to skip. It also keeps the
                two folds on this panel distinguishable.
              */}
              <details className="mt-1">
                <summary className="text-xs cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  Why is this empty?
                </summary>
                <ul className="text-xs mt-1 ml-4 list-disc space-y-1">
                  <li>
                    Subscription data is brought across per environment; if that
                    hasn&apos;t happened here, nothing shows.
                  </li>
                  <li>
                    Subscriptions that ended long ago were never brought across.
                  </li>
                </ul>
              </details>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {currentCount} current · {pastCount} past
                {failingCount > 0
                  ? ` · ${failingCount} with payment failing`
                  : ''}
              </p>

              {currentCount > 0 && (
                <ul className="space-y-3">
                  {current.map(entry => (
                    <SubscriptionCard
                      key={entry.row.id}
                      entry={entry}
                      catalog={catalog}
                    />
                  ))}
                  {unmirroredCurrent.map(grant => (
                    <UnmirroredGrantCard
                      key={`${grant.provider}:${grant.providerSubscriptionId ?? 'legacy'}`}
                      grant={grant}
                    />
                  ))}
                </ul>
              )}

              {currentCount === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No current subscription.
                </p>
              )}

              {stillGranting.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">
                    Ended, but access not removed ({stillGranting.length})
                  </p>
                  <ul className="space-y-3">
                    {stillGranting.map(entry => (
                      <SubscriptionCard
                        key={entry.row.id}
                        entry={entry}
                        catalog={catalog}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {foldedCount > 0 && (
                <details
                  className="mt-3"
                  // Opens only when there is nothing current to look at
                  // instead. An alarm no longer forces it: those rows are
                  // hoisted above, so a long history cannot ride in on one.
                  open={currentCount === 0 && stillGranting.length === 0}
                >
                  <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    Past subscriptions ({foldedCount})
                  </summary>
                  <ul className="space-y-3 mt-2">
                    {settledPast.map(entry => (
                      <SubscriptionCard
                        key={entry.row.id}
                        entry={entry}
                        catalog={catalog}
                      />
                    ))}
                    {unmirroredPast.map(grant => (
                      <UnmirroredGrantCard
                        key={`${grant.provider}:${grant.providerSubscriptionId ?? 'legacy'}`}
                        grant={grant}
                      />
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}

          {page?.hasMore && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
              More than {PAGE_SIZE} subscriptions are mirrored for this user;
              only the {PAGE_SIZE} most recently updated are shown, and grants
              held only in the access record are not derived at all while that
              is true.{' '}
              <Link
                to="/admin/subscriptions"
                className="underline hover:text-amber-900 dark:hover:text-amber-300"
              >
                Open the console
              </Link>{' '}
              and filter by this uid to page through them all.
            </p>
          )}
        </>
      )}
    </div>
  );
}
